import React, { useEffect, useState } from 'react';
import { useTranslations } from '../translations/translations';
import { useFadeIn } from '../hooks/useFadeIn';

const GlobalPresence = () => {
  const t = useTranslations();
  const [visiblePoints, setVisiblePoints] = useState(new Set());
  const [cycle, setCycle] = useState(0);
  const [ref, isVisible] = useFadeIn(0.1);

  // Coordonnées réelles des villes (latitude/longitude converties en pourcentages de la carte)
  const locations = [
    // France
    { name: 'Paris', x: 50.5, y: 35, region: 'France' },
    { name: 'Lyon', x: 50.8, y: 38, region: 'France' },
    { name: 'Marseille', x: 51, y: 41, region: 'France' },
    { name: 'Bordeaux', x: 49.5, y: 38.5, region: 'France' },
    { name: 'Nice', x: 52, y: 41.5, region: 'France' },
    { name: 'Monaco', x: 52.2, y: 41.5, region: 'France' },
    { name: 'Cannes', x: 51.9, y: 41.6, region: 'France' },
    { name: 'Saint-Tropez', x: 52.1, y: 41.8, region: 'France' },
    
    // Méditerranée
    { name: 'Ibiza', x: 50.2, y: 43, region: 'Méditerranée' },
    { name: 'Barcelone', x: 50.3, y: 42, region: 'Méditerranée' },
    { name: 'Athènes', x: 55, y: 44, region: 'Méditerranée' },
    { name: 'Santorin', x: 56, y: 45, region: 'Méditerranée' },
    { name: 'Mykonos', x: 56.2, y: 44.8, region: 'Méditerranée' },
    
    // Mer Méditerranée (points dans l'eau)
    { name: '', x: 52.5, y: 44, region: 'Mer' },
    { name: '', x: 53.5, y: 45.5, region: 'Mer' },
    { name: '', x: 54.2, y: 43.5, region: 'Mer' },
    
    // Europe
    { name: 'Londres', x: 49.8, y: 32, region: 'Europe' },
    { name: 'Genève', x: 51.5, y: 37.5, region: 'Europe' },
    { name: 'Milan', x: 52, y: 38.5, region: 'Europe' },
    { name: 'Rome', x: 53, y: 42, region: 'Europe' },
    
    // Moyen-Orient
    { name: 'Dubaï', x: 62, y: 47, region: 'Moyen-Orient' },
    { name: 'Abu Dhabi', x: 61.5, y: 47.5, region: 'Moyen-Orient' },
    
    // Amérique
    { name: 'New York', x: 27, y: 35, region: 'Amérique' },
    { name: 'Miami', x: 25, y: 48, region: 'Amérique' },
    { name: 'Rio de Janeiro', x: 32, y: 65, region: 'Amérique' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      // Choisir aléatoirement entre 5 et 8 points à afficher
      const numberOfPoints = 5 + Math.floor(Math.random() * 4);
      const randomIndices = new Set();
      
      // Deux villes trop proches ne s'allument pas ensemble, pour que leurs noms ne se chevauchent pas
      const isTooClose = (a, b) => Math.hypot((a.x - b.x) * 2, a.y - b.y) < 7;
      let attempts = 0;
      while (randomIndices.size < numberOfPoints && attempts < 200) {
        attempts++;
        const candidate = Math.floor(Math.random() * locations.length);
        if ([...randomIndices].some(i => isTooClose(locations[i], locations[candidate]))) continue;
        randomIndices.add(candidate);
      }
      
      setVisiblePoints(randomIndices);
      setCycle(c => c + 1);
    }, 1800); // Change toutes les 1.8 secondes

    return () => clearInterval(interval);
  }, [locations]);

  return (
    <section style={{ 
      padding: '5rem 0', 
      background: '#000',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="container-custom">
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '3rem',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{ 
            fontSize: '3rem', 
            fontWeight: 200,
            marginBottom: '1rem',
            color: '#fff',
            letterSpacing: '-0.02em'
          }}>
            {t.globalPresence.title}
          </h2>
          <p style={{ 
            fontSize: '1.1rem', 
            color: '#737373',
            maxWidth: '900px',
            margin: '0 auto',
            lineHeight: 1.6,
            marginBottom: '1rem'
          }}>
            {t.globalPresence.text1}
          </p>
          <p style={{ 
            fontSize: '1.1rem', 
            color: '#737373',
            maxWidth: '900px',
            margin: '0 auto',
            lineHeight: 1.6,
            marginBottom: '1rem'
          }}>
            {t.globalPresence.text2}
          </p>
          <p style={{ 
            fontSize: '1.1rem', 
            color: '#737373',
            maxWidth: '900px',
            margin: '0 auto',
            lineHeight: 1.6,
            marginBottom: '1.5rem'
          }}>
            {t.globalPresence.text3}
          </p>
          
          {/* Liste des villes pour SEO */}
          <div style={{ 
            maxWidth: '900px',
            margin: '0 auto',
            padding: '2rem 0',
            borderTop: '1px solid #1a1a1a'
          }}>
            <p style={{ 
              fontSize: '0.85rem', 
              color: '#525252',
              lineHeight: 1.8,
              textAlign: 'center',
              fontWeight: 300
            }}>
              <strong style={{ color: '#737373' }}>{t.globalPresence.cities}:</strong> Paris, Lyon, Marseille, Bordeaux, Nice, Monaco, Cannes, Saint-Tropez, Londres, Genève, Milan, Rome, Barcelone, Ibiza, Athènes, Santorin, Mykonos, Dubaï, Abu Dhabi, New York, Miami, Rio de Janeiro
            </p>
          </div>
        </div>

        {/* Carte du monde RÉELLE */}
        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          aspectRatio: '2/1',
          background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.9) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.05)',
          overflow: 'hidden'
        }}>
          {/* Vraie carte du monde en SVG */}
          <img 
            src="/assets/Equirectangular_projection_SW.jpg"
            alt="World Map"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'grayscale(100%) brightness(0.2) contrast(1.5)',
              opacity: 0.4
            }}
          />

          {/* Grille par-dessus */}
          <svg 
            viewBox="0 0 1000 500" 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.05,
              pointerEvents: 'none'
            }}
          >
            {[...Array(20)].map((_, i) => (
              <line 
                key={`h${i}`}
                x1="0" 
                y1={i * 25} 
                x2="1000" 
                y2={i * 25}
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="0.5"
              />
            ))}
            {[...Array(40)].map((_, i) => (
              <line 
                key={`v${i}`}
                x1={i * 25} 
                y1="0" 
                x2={i * 25} 
                y2="500"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="0.5"
              />
            ))}
          </svg>

          {/* Réseau : arcs lumineux depuis Paris vers les villes actives */}
          <svg
            viewBox="0 0 1000 500"
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none'
            }}
          >
            {locations.map((location, index) => {
              if (!visiblePoints.has(index) || !location.name || location.name === 'Paris') return null;
              const x1 = 505, y1 = 175;
              const x2 = location.x * 10, y2 = location.y * 5;
              const distance = Math.hypot(x2 - x1, y2 - y1);
              const cx = (x1 + x2) / 2, cy = Math.min(y1, y2) - distance * 0.35;
              return (
                <path
                  key={`arc-${index}-${cycle}`}
                  d={`M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`}
                  fill="none"
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  pathLength="1"
                  style={{
                    strokeDasharray: 1,
                    strokeDashoffset: 1,
                    animation: 'drawArc 1.6s ease-out forwards'
                  }}
                />
              );
            })}
          </svg>

          {/* Toutes les villes scintillent en permanence */}
          {locations.map((location, index) => (
            <div
              key={`twinkle-${index}`}
              style={{
                position: 'absolute',
                left: `${location.x}%`,
                top: `${location.y}%`,
                width: '5px',
                height: '5px',
                marginLeft: '-2.5px',
                marginTop: '-2.5px',
                background: '#fff',
                borderRadius: '50%',
                boxShadow: '0 0 6px rgba(255,255,255,0.9), 0 0 12px rgba(255,255,255,0.5)',
                animation: `twinkle ${2.2 + (index % 5) * 0.35}s ease-in-out ${(index * 0.37) % 3}s infinite`,
                pointerEvents: 'none'
              }}
            />
          ))}

          {/* Points scintillants qui apparaissent/disparaissent */}
          {locations.map((location, index) => {
            const isVisible = visiblePoints.has(index);
            // Créer une clé unique basée sur le nom et la position
            const uniqueKey = location.name 
              ? `${location.name}-${location.x}-${location.y}` 
              : `point-${location.x}-${location.y}-${location.region}`;
            
            return (
              <div
                key={uniqueKey}
                style={{
                  position: 'absolute',
                  left: `${location.x}%`,
                  top: `${location.y}%`,
                  transform: 'translate(-50%, -50%)',
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.8s ease',
                  pointerEvents: 'none'
                }}
              >
                {/* Point lumineux */}
                <div style={{
                  width: location.name ? '20px' : '14px',
                  height: location.name ? '20px' : '14px',
                  background: '#fff',
                  borderRadius: '50%',
                  position: 'relative',
                  zIndex: 2,
                  boxShadow: `
                    0 0 15px rgba(255,255,255,0.9),
                    0 0 30px rgba(255,255,255,0.7),
                    0 0 45px rgba(255,255,255,0.4)
                  `,
                  animation: isVisible ? 'glow 1.5s ease-in-out infinite' : 'none'
                }} />
                
                {/* Cercle d'expansion */}
                {location.name && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '60px',
                    height: '60px',
                    border: '2px solid rgba(255,255,255,0.5)',
                    borderRadius: '50%',
                    animation: isVisible ? 'expandFade 2s ease-out infinite' : 'none'
                  }} />
                )}
                {location.name && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '60px',
                    height: '60px',
                    border: '1px solid rgba(255,255,255,0.4)',
                    borderRadius: '50%',
                    animation: isVisible ? 'expandFade 2s ease-out 1s infinite' : 'none'
                  }} />
                )}

                {/* Nom de la ville */}
                {location.name && (
                  <div style={{
                    position: 'absolute',
                    top: '18px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                    color: '#fff',
                    fontWeight: 600,
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.5s ease 0.3s',
                    letterSpacing: '0.5px',
                    textShadow: `
                      0 2px 10px rgba(0,0,0,0.9),
                      0 0 20px rgba(255,255,255,0.3)
                    `,
                    background: 'rgba(0,0,0,0.6)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    {location.name}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="presence-stats" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '3rem',
          marginTop: '5rem',
          maxWidth: '1100px',
          margin: '5rem auto 0'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '3rem', 
              fontWeight: 200, 
              color: '#fff',
              marginBottom: '0.5rem'
            }}>
              20+
            </div>
            <div style={{ 
              fontSize: '0.9rem', 
              color: '#737373',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {t.globalPresence.stats.cities}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '3rem', 
              fontWeight: 200, 
              color: '#fff',
              marginBottom: '0.5rem'
            }}>
              3
            </div>
            <div style={{ 
              fontSize: '0.9rem', 
              color: '#737373',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {t.globalPresence.stats.continents}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '3rem', 
              fontWeight: 200, 
              color: '#fff',
              marginBottom: '0.5rem'
            }}>
              24/7
            </div>
            <div style={{ 
              fontSize: '0.9rem', 
              color: '#737373',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {t.globalPresence.stats.availability}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '3rem', 
              fontWeight: 200, 
              color: '#fff',
              marginBottom: '0.5rem'
            }}>
              100%
            </div>
            <div style={{ 
              fontSize: '0.9rem', 
              color: '#737373',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
              {t.globalPresence.stats.confidentiality}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes glow {
          0%, 100% {
            box-shadow: 
              0 0 10px rgba(255,255,255,0.9),
              0 0 20px rgba(255,255,255,0.6),
              0 0 30px rgba(255,255,255,0.3);
          }
          50% {
            box-shadow: 
              0 0 15px rgba(255,255,255,1),
              0 0 30px rgba(255,255,255,0.8),
              0 0 45px rgba(255,255,255,0.5);
          }
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.25; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.3); }
        }

        @keyframes drawArc {
          0% { stroke-dashoffset: 1; opacity: 1; }
          70% { stroke-dashoffset: 0; opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0.35; }
        }

        @keyframes expandFade {
          0% {
            width: 40px;
            height: 40px;
            opacity: 0.8;
          }
          100% {
            width: 80px;
            height: 80px;
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default GlobalPresence;
